<script setup lang="ts">
import { useEditor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import { Image as TiptapImage } from '@tiptap/extension-image'
import { DynamicContentDecorator } from './extensions/DynamicContentDecorator'
import { ref, watch } from "vue";
import {
  Undo2,
  Redo2,
  Bold,
  Italic,
  Strikethrough,
  Pilcrow,
  Link,
  Unlink,
  Heading,
  List,
  ListOrdered,
  BrushCleaning,
  CodeXml,
  Image,
} from '@lucide/vue';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/shadcn/components/ui/dropdown-menu";
import { Dialog, DialogTrigger } from "@/shadcn/components/ui/dialog";
import { DialogHeader, DialogScrollContent, DialogTitle } from "@/components/ui/dialog";
import MediaLibrary from "@/components/marketing-message/MediaLibrary.vue";

const props = withDefaults(defineProps<{
  name: string,
  value?: string,
  withMediaLibrary: boolean,
  mediaCollectionName?: string | undefined,
  mediaUploadUrl?: string | undefined,
  media?: Array<any>,
}>(), {
  value: '',
  withMediaLibrary: false,
})

const content = ref(props.value?.split("\u21b5").join(''));

watch(() => props.value, (value) => {
  content.value = value?.split("\u21b5").join('');
})

const emit = defineEmits([
  'input',
  'mediaCollectionUpdated',
])

const editor = useEditor({
  content: content.value,
  extensions: [
    StarterKit.configure({
      underline: false,
      link: {
        openOnClick: false,
        autolink: true,
        defaultProtocol: 'https',
        protocols: ['http', 'https', 'mailto', 'tel'],
      },
    }),
    DynamicContentDecorator,
    TiptapImage.configure({
      inline: true,
    }),
  ],
  onUpdate: ({editor}) => {
    emit('input', editor.getHTML())
    content.value = editor.getHTML()
  }
})

const setLink = function() {
  const previousUrl = editor.value.getAttributes('link').href
  const url = window.prompt('URL', previousUrl)

  // cancelled
  if (url === null) {
    return
  }

  // empty
  if (url === '') {
    editor.value
      .chain()
      .focus()
      .extendMarkRange('link')
      .unsetLink()
      .run()

    return
  }

  // update link
  editor.value
    .chain()
    .focus()
    .extendMarkRange('link')
    .setLink({ href: url })
    .run()
}


const showSource = ref(false);
const toggleSourceMode = () => {
  showSource.value = !showSource.value;
}
const updateEditorContent = () => {
  if (editor.value) {
    editor.value.commands.setContent(content.value, { emitUpdate: false });
  }
}

const setContent = (value: string) => {
  content.value = value
  updateEditorContent()
  emit('input', value)
}

const onHiddenInput = (event: Event) => {
  const target = event.target
  if (!(target instanceof HTMLTextAreaElement)) {
    return
  }

  setContent(target.value)
}

const showMediaLibrary = ref(false);
const onFilesUploaded = (files) => {
  emit('mediaCollectionUpdated', files);
}

const onMediaSelected = (mediaItem) => {
  if (mediaItem && mediaItem.url) {
    editor.value
      .chain()
      .focus()
      .setImage({ src: mediaItem.url })
      .run()
  }
  showMediaLibrary.value = false;
}
</script>

<template>
  <div v-if="editor" class="tiptap_editor-container">
    <div class="tiptap_editor-toolbar tw:overflow-x-auto border-bottom tw:shrink-0">
      <div class="btn-group  btn-group-sm">
        <button type="button" @click="editor.chain().focus().undo().run()" :disabled="!editor.can().chain().focus().undo().run()" class="btn btn-light" :title="$t('Undo')">
          <Undo2 class="tw:w-4"/>
        </button>
        <button type="button" @click="editor.chain().focus().redo().run()" :disabled="!editor.can().chain().focus().redo().run()" class="btn btn-light" :title="$t('Redo')">
          <Redo2 class="tw:w-4"/>
        </button>
        <!-- Heading -->
        <div class="btn-group" role="group">
          <DropdownMenu>
            <DropdownMenuTrigger :title="$t('Headings')" class="btn btn-light dropdown-toggle tw:flex tw:items-center">
              <Heading class="tw:w-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
                <DropdownMenuItem @click="editor.chain().focus().toggleHeading({ level: 2 }).run()" :class="[editor.isActive('heading', { level: 2 }) ? 'active' : '' ]" :title="$t('Heading 2')">
                  H2
                </DropdownMenuItem>
                <DropdownMenuItem @click="editor.chain().focus().toggleHeading({ level: 3 }).run()" :class="[editor.isActive('heading', { level: 3 }) ? 'active' : '' ]" :title="$t('Heading 3')">
                  H3
                </DropdownMenuItem>
                <DropdownMenuItem @click="editor.chain().focus().toggleHeading({ level: 4 }).run()" :class="[editor.isActive('heading', { level: 4 }) ? 'active' : '' ]" :title="$t('Heading 4')">
                  H4
                </DropdownMenuItem>
                <DropdownMenuItem @click="editor.chain().focus().toggleHeading({ level: 5 }).run()" :class="[editor.isActive('heading', { level: 5 }) ? 'active' : '' ]" :title="$t('Heading 5')">
                  H5
                </DropdownMenuItem>
                <DropdownMenuItem @click="editor.chain().focus().toggleHeading({ level: 6 }).run()" :class="[editor.isActive('heading', { level: 6 }) ? 'active' : '' ]" :title="$t('Heading 6')">
                  H6
                </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <button type="button" @click="editor.chain().focus().toggleBold().run()" :disabled="!editor.can().chain().focus().toggleBold().run()" :class="['btn btn-light', editor.isActive('bold') ? 'active' : '' ]" :title="$t('Bold')">
          <Bold class="tw:w-4"/>
        </button>
        <button type="button" @click="editor.chain().focus().toggleItalic().run()" :disabled="!editor.can().chain().focus().toggleItalic().run()" :class="['btn btn-light', editor.isActive('italic') ? 'active' : '' ]" :title="$t('Italic')">
          <Italic class="tw:w-4"/>
        </button>
        <button type="button" @click="editor.chain().focus().toggleStrike().run()" :disabled="!editor.can().chain().focus().toggleStrike().run()" :class="['btn btn-light', editor.isActive('strike') ? 'active' : '' ]" :title="$t('Strikethrough')">
          <Strikethrough class="tw:w-4"/>
        </button>
        <button type="button" @click="editor.chain().focus().setParagraph().run()" :class="['btn btn-light', editor.isActive('paragraph') ? 'active' : '' ]" :title="$t('Paragraph')">
          <Pilcrow class="tw:w-4"/>
        </button>
        <button type="button" @click="editor.chain().focus().toggleBulletList().run()" :class="['btn btn-light', editor.isActive('bulletList') ? 'active' : '' ]" :title="$t('Bullet list')">
          <List class="tw:w-4"/>
        </button>
        <button type="button" @click="editor.chain().focus().toggleOrderedList().run()" :class="['btn btn-light', editor.isActive('orderedList') ? 'active' : '' ]" :title="$t('Numbered list')">
          <ListOrdered class="tw:w-4"/>
        </button>
        <button type="button" @click="setLink" :class="['btn btn-light', editor.isActive('link') ? 'active' : '' ]" :title="$t('Add a link')">
          <Link class="tw:w-4"/>
        </button>
        <button @click="editor.chain().focus().unsetLink().run()" :disabled="!editor.isActive('link')"  class="btn btn-light" :title="$t('Remove the link')">
          <Unlink class="tw:w-4"/>
        </button>
        <template v-if="props.withMediaLibrary">
          <Dialog :open="showMediaLibrary" @update:open="showMediaLibrary = $event">
            <DialogTrigger class="btn btn-light" :title="$t('Add an image')">
              <Image class="tw:w-4" />
            </DialogTrigger>
            <DialogScrollContent size="lg">
              <DialogHeader>
                <DialogTitle>
                  {{ $t("Media Library") }}
                </DialogTitle>
              </DialogHeader>

              <MediaLibrary
                :collection-name="props.mediaCollectionName"
                :upload-url="props.mediaUploadUrl"
                :media="props.media"
                @files-uploaded="onFilesUploaded($event)"
                @media-selected="onMediaSelected($event)"
              />

            </DialogScrollContent>
          </Dialog>
        </template>
        <button type="button" @click="editor.chain().focus().clearNodes().run()" class="btn btn-light" :title="$t('Clear formatting')">
          <BrushCleaning class="tw:w-4"/>
        </button>
        <button type="button" @click="toggleSourceMode" :class="['btn btn-light', showSource ? 'active' : '']" :title="$t('Source code')">
          <CodeXml class="tw:w-4"/>
        </button>
      </div>
    </div>
    <template v-if="!showSource">
      <EditorContent :editor="editor" class="tiptap_editor-content"></EditorContent>
    </template>
    <template v-else>
      <textarea v-model="content" class="tiptap_source-code-textarea tw:resize-y tw:min-h-48 tw:text-sm" @input="updateEditorContent"></textarea>
    </template>

    <!-- A value set from outside, such as a TranslatableForm copying another language, reaches the editor. -->
    <textarea class="form-control" :name="name" :value="content" style="display:none" @input="onHiddenInput"></textarea>
  </div>
</template>

<style>
/** voir https://github.com/ueberdosis/tiptap/issues/526#issuecomment-1761639866  pour passage en tailwind **/
.ProseMirror:focus {
  outline: none;
}
.tiptap_editor-container{
  outline: none;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-height: 30rem;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  color: #495057;
  background-color: #fff;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  overflow: hidden;
}

.tiptap_editor-toolbar {
  background-color: #f8f9fa;
  border-bottom: 1px solid #ced4da;
  position: sticky;
  top: 0;
  z-index: 1;
}

.tiptap_editor-toolbar .btn-group-sm > .btn {
  padding: 0.25rem 0.8rem;
  border: 0;
  border-bottom-left-radius: 0;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.tiptap_editor-toolbar .btn-group-sm > .btn:disabled {
  opacity: 0.3;
}

.tiptap_editor-content {
  flex: 1;
  padding: 0.375rem 0.75rem;
  overflow-y: auto;
}

.tiptap_source-code-textarea {
  font-family: monospace;
  border: none;
  width: 100%;
  height: 100%;
}


.ProseMirror {
  height: 100%;
}


/* Match the frontoffice (Bootstrap) paragraph spacing so Enter and Shift+Enter look different */
.ProseMirror p {
  margin-bottom: 1rem;
}

/* Define UL and OL styles */
.ProseMirror ul, .ProseMirror ol {
  padding-left: 1.5rem;
  margin: 0.5rem 0;
}

.ProseMirror ul {
  list-style-type: disc;
}

.ProseMirror ol {
  list-style-type: decimal;
}

.ProseMirror li {
  margin: 0.25rem 0;
  line-height: 1.5;
}

.ProseMirror li > ul,
.ProseMirror li > ol {
  margin: 0.25rem 0;
}

.ProseMirror ul ul {
  list-style-type: circle;
}

.ProseMirror ul ul ul {
  list-style-type: square;
}

.ProseMirror ol ol {
  list-style-type: lower-alpha;
}

.ProseMirror ol ol ol {
  list-style-type: lower-roman;
}

.ProseMirror img {
  display: block;
  height: auto;
  margin: 1.5rem 0;
  max-width: 100%;
}

.ProseMirror img.ProseMirror-selectednode {
  outline: 3px solid var(--primary);
}

</style>
